<div class="container-fluid complyon-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title mb-2 text-l">Ethics &amp; whistleblowing</h1>
      <p class="complyon-subtitle mb-0 text-s">Protected disclosures, ethics complaints, gifts and outside interests - handled confidentially with investigation workflow and retaliation tracking.</p>
    </section>

    <section class="row g-3 mb-3">
      <div v-for="card in cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
        <article class="complyon-card rounded-4 p-3 h-100">
          <div class="complyon-stat-label text-xs">{{ card.label }}</div>
          <div class="complyon-stat-value text-l" :class="card.accent === 'red' ? 'text-danger' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-dark'">{{ card.value }}</div>
          <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
        </article>
      </div>
    </section>

    <section class="complyon-panel rounded-4 overflow-hidden">
      <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 px-3 pt-3">
        <div class="min-w-0">
          <div class="complyon-section-title text-s">Ethics &amp; whistleblowing</div>
          <div class="complyon-section-subtitle text-xs">Protected disclosures, ethics complaints, gifts and outside interests - handled confidentially with an investigation workflow, retaliation tracking and links to the procurement, conflict and incident records they touch.</div>
        </div>
        <button type="button" class="btn config-navbar-primary-action finance-action-btn">
          <i class="bi bi-plus-lg"></i>
          <span>Log matter</span>
        </button>
      </div>

      <div class="d-flex flex-wrap align-items-center gap-2 px-3 py-2 border-top border-light-subtle">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span class="doa-tab-count">{{ tab.count }}</span>
        </button>

        <div class="input-group input-group-sm ms-auto ethics-table-search">
          <span class="input-group-text bg-white border-end-0">
            <i class="bi bi-search text-secondary"></i>
          </span>
          <input v-model="search_query" type="text" class="form-control border-start-0 shadow-none" placeholder="Search...">
        </div>
      </div>

      <div class="table-responsive">
        <table class="table complyon-table align-middle mb-0">
          <thead>
            <tr>
              <th class="ps-3 text-xs">Ref</th>
              <th class="text-xs">Matter</th>
              <th class="text-xs">Type</th>
              <th class="text-xs">Confidentiality</th>
              <th class="text-xs">Status</th>
              <th class="pe-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filtered_rows"
              :key="row.ref"
              class="ethics-table-row"
              :class="{ 'table-active': active_drawer_ref === row.ref }"
              role="button"
              tabindex="0"
              @click="open_drawer(row.ref)"
              @keyup.enter="open_drawer(row.ref)"
              @keyup.space.prevent="open_drawer(row.ref)"
            >
              <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
              <td class="fw-semibold">{{ row.matter }}</td>
              <td>{{ row.type }}</td>
              <td>{{ row.confidentiality }}</td>
              <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
              <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
            </tr>
            <tr v-if="!filtered_rows.length">
              <td colspan="6" class="px-3 py-4 text-center text-secondary">No matters match the current filter.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>

  <teleport to="body">
    <div v-if="active_drawer_data" class="audit-drawer-backdrop" @click.self="close_drawer">
      <aside class="audit-drawer" role="dialog" aria-modal="true" aria-labelledby="ethics-drawer-title">
        <div class="audit-drawer-header">
          <div class="d-flex align-items-start justify-content-between gap-3">
            <div class="min-w-0">
              <div class="audit-drawer-kicker">Ethics - {{ active_drawer_data.ref }}</div>
              <h2 id="ethics-drawer-title" class="audit-drawer-title mb-1">{{ active_drawer_data.matter }}</h2>
              <div class="audit-drawer-subtitle">{{ active_drawer_data.subtitle }}</div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span class="badge rounded-pill audit-drawer-stage-pill" :class="active_drawer_data.status.class">{{ active_drawer_data.status.label }}</span>
              <button type="button" class="btn audit-drawer-close" aria-label="Close ethics drawer" @click="close_drawer">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="audit-drawer-body">
          <section class="audit-drawer-section">
            <div class="audit-finding-detail-grid">
              <div v-for="detail in active_drawer_data.details" :key="`${active_drawer_data.ref}-${detail[0]}`" class="audit-finding-detail-row">
                <div class="audit-finding-detail-label">{{ detail[0] }}</div>
                <div class="audit-finding-detail-value">{{ detail[1] }}</div>
              </div>
            </div>
          </section>

          <section v-if="active_drawer_data.connected_records.length" class="audit-drawer-section">
            <div class="audit-drawer-section-title">Connected records</div>
            <div class="audit-finding-record-grid">
              <article v-for="record in active_drawer_data.connected_records" :key="`${active_drawer_data.ref}-${record[0]}-${record[1]}`" class="audit-finding-record-card">
                <div class="d-flex align-items-start justify-content-between gap-3">
                  <div class="d-flex align-items-start gap-2 min-w-0">
                    <span class="audit-finding-record-icon">
                      <i class="bi" :class="record[2]"></i>
                    </span>
                    <div class="min-w-0">
                      <div class="audit-finding-record-type">{{ record[0] }}</div>
                      <div class="audit-finding-record-title">{{ record[1] }}</div>
                    </div>
                  </div>
                  <i class="bi bi-box-arrow-up-right audit-drawer-finding-arrow"></i>
                </div>
              </article>
            </div>
          </section>

          <section class="audit-drawer-section">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <div class="audit-drawer-section-title">Recommended action</div>
              <span class="badge rounded-pill bg-success-subtle text-success-emphasis">AI</span>
            </div>
            <div class="ethics-drawer-ai-card">{{ active_drawer_data.recommendation }}</div>
          </section>
        </div>

        <div class="audit-drawer-footer d-flex align-items-center gap-2 flex-wrap">
          <button type="button" class="btn audit-inline-link">
            <i class="bi bi-flag"></i>
            <span>Watch</span>
          </button>
          <div class="d-flex align-items-center gap-2 ms-auto">
            <button type="button" class="btn btn-light">Evidence</button>
            <button type="button" class="btn config-navbar-primary-action finance-action-btn">{{ active_drawer_data.primary_action }}</button>
          </div>
        </div>
      </aside>
    </div>
  </teleport>
</div>
